/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type PrCounts = {
  involves: number;
  authored: number;
  reviewRequested: number;
};

// Shared by PendingWorkCard, the PR card, and the /pull-requests page so one
// Home render produces at most ONE /counts round-trip per login (in-flight
// dedup) and repeat visits inside the TTL produce none.
const TTL_MS = 2 * 60 * 1000;
const cache = new Map<string, { ts: number; v: PrCounts }>();
const inflight = new Map<string, Promise<PrCounts>>();

export const fetchPrCounts = async (
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  asLogin?: string,
): Promise<PrCounts> => {
  const key = asLogin ?? '__self__';
  const hit = cache.get(key);
  if (hit && Date.now() - hit.ts < TTL_MS) return hit.v;
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = (async () => {
    const base = await discoveryApi.getBaseUrl('pull-requests-reader');
    const url = `${base}/counts${asLogin ? `?as=${encodeURIComponent(asLogin)}` : ''}`;
    const res = await fetchApi.fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    const body = (await res.json()) as PrCounts;
    const v: PrCounts = {
      involves: body.involves ?? 0,
      authored: body.authored ?? 0,
      reviewRequested: body.reviewRequested ?? 0,
    };
    cache.set(key, { ts: Date.now(), v });
    return v;
  })();
  inflight.set(key, p);
  try {
    return await p;
  } finally {
    inflight.delete(key);
  }
};
